import React, { useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { useAuth } from "@/contexts/AuthContext";
import { useCart } from "@/contexts/CartContext";
import { Menu, X, LogOut, ShoppingCart, LogIn, UserPlus, ShieldCheck } from "lucide-react";
import PartnersStrip from "@/components/PartnersStrip";
import ChatWidget from "@/components/ChatWidget";

export default function Layout() {
    const { user, logout } = useAuth();
    const { totals } = useCart();
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();

    // Primary menu — same 2 items for everyone. Add "Mes commandes" only when logged-in as pro.
    const menu = [
        { to: "/tarifs", label: "Liste des prix" },
        { to: "/suggestions", label: "Suggestions de la maison" },
    ];
    if (user?.role === "pro" && user?.status === "approved") {
        menu.push({ to: "/mes-commandes", label: "Mes commandes" });
        menu.push({ to: "/mon-compte", label: "Mon compte" });
    }

    const showCartPill = user?.role === "pro" && user?.status === "approved";
    const isAdmin = user?.role === "admin";

    const doLogout = async () => {
        await logout();
        setOpen(false);
        navigate("/");
    };

    const isActive = (to) => location.pathname === to || location.pathname.startsWith(to + "/");

    return (
        <div className="min-h-screen flex flex-col bg-black">
            <header
                className="sticky top-0 z-40 border-b"
                style={{ background: "#000", borderColor: "rgba(212,188,124,0.15)" }}
                data-testid="site-header"
            >
                <div className="max-w-[1600px] mx-auto px-4 md:px-8 py-3 md:py-4 flex items-center justify-between gap-4">
                    {/* Left: Logo */}
                    <Logo size="lg" />

                    {/* Center: menu (desktop) */}
                    <nav className="hidden lg:flex items-center gap-8 xl:gap-10 flex-1 justify-center">
                        {menu.map((it) => (
                            <Link
                                key={it.to}
                                to={it.to}
                                className={`text-xs font-medium transition-colors uppercase ${
                                    isActive(it.to) ? "text-[#D4BC7C]" : "text-[#F2ECE0] hover:text-[#D4BC7C]"
                                }`}
                                style={{
                                    fontFamily: "Montserrat, sans-serif",
                                    letterSpacing: "0.18em",
                                }}
                                data-testid={`nav-${it.label.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                            >
                                {it.label}
                            </Link>
                        ))}
                    </nav>

                    {/* Right: action pills */}
                    <div className="hidden md:flex items-center gap-2 flex-shrink-0">
                        {!user ? (
                            <>
                                <Link to="/connexion" className="pill-btn pill-btn--outline" data-testid="pill-login">
                                    <LogIn size={14} strokeWidth={2.2} />
                                    Se connecter
                                </Link>
                                <Link to="/inscription" className="pill-btn" data-testid="pill-register">
                                    <UserPlus size={14} strokeWidth={2.2} />
                                    Demander l'accès
                                </Link>
                            </>
                        ) : (
                            <>
                                {isAdmin && (
                                    <Link
                                        to="/admin/tableau-de-bord"
                                        className={`pill-btn ${
                                            location.pathname.startsWith("/admin")
                                                ? "pill-btn--active"
                                                : "pill-btn--outline"
                                        }`}
                                        data-testid="pill-admin"
                                    >
                                        <ShieldCheck size={14} strokeWidth={2.2} />
                                        Administration
                                    </Link>
                                )}
                                {showCartPill && (
                                    <Link
                                        to="/panier"
                                        className={`pill-btn ${totals.count > 0 ? "" : "pill-btn--outline"}`}
                                        data-testid="pill-cart"
                                    >
                                        <ShoppingCart size={14} strokeWidth={2.2} />
                                        Panier
                                        {totals.count > 0 && (
                                            <span
                                                className="ml-1 px-1.5 py-0.5 text-[10px] font-bold"
                                                style={{ background: "#000", color: "#D4BC7C", borderRadius: 999 }}
                                                data-testid="cart-badge"
                                            >
                                                {totals.count}
                                            </span>
                                        )}
                                    </Link>
                                )}
                                <button
                                    onClick={doLogout}
                                    className="pill-btn pill-btn--outline"
                                    data-testid="pill-logout"
                                >
                                    <LogOut size={14} strokeWidth={2.2} />
                                    Déconnexion
                                </button>
                            </>
                        )}
                    </div>

                    {/* Mobile: cart + hamburger */}
                    <div className="md:hidden flex items-center gap-2">
                        {showCartPill && (
                            <Link
                                to="/panier"
                                className="relative inline-flex items-center text-[#D4BC7C] p-2"
                                aria-label="Panier"
                                data-testid="nav-cart-mobile"
                            >
                                <ShoppingCart size={22} />
                                {totals.count > 0 && (
                                    <span
                                        className="absolute top-0 right-0 px-1 py-0 text-[10px] font-bold"
                                        style={{ background: "#D4BC7C", color: "#000", minWidth: "18px", textAlign: "center" }}
                                    >
                                        {totals.count}
                                    </span>
                                )}
                            </Link>
                        )}
                        <button
                            className="text-[#D4BC7C] p-2"
                            onClick={() => setOpen(!open)}
                            aria-label="Menu"
                            data-testid="nav-mobile-toggle"
                        >
                            {open ? <X size={26} /> : <Menu size={26} />}
                        </button>
                    </div>
                </div>

                {/* Mobile drawer */}
                {open && (
                    <div
                        className="lg:hidden border-t"
                        style={{ borderColor: "rgba(212,188,124,0.2)" }}
                        data-testid="nav-mobile-menu"
                    >
                        <div className="px-5 py-6 flex flex-col gap-4 bg-black">
                            {menu.map((it) => (
                                <Link
                                    key={it.to}
                                    to={it.to}
                                    onClick={() => setOpen(false)}
                                    className={`text-xs py-1 font-medium uppercase ${
                                        isActive(it.to) ? "text-[#D4BC7C]" : "text-[#F2ECE0]"
                                    }`}
                                    style={{
                                        fontFamily: "Montserrat, sans-serif",
                                        letterSpacing: "0.18em",
                                    }}
                                >
                                    {it.label}
                                </Link>
                            ))}
                            {isAdmin && (
                                <Link
                                    to="/admin/tableau-de-bord"
                                    onClick={() => setOpen(false)}
                                    className="text-xs py-1 font-medium text-[#D4BC7C] uppercase"
                                    style={{ fontFamily: "Montserrat, sans-serif", letterSpacing: "0.18em" }}
                                >
                                    Administration
                                </Link>
                            )}
                            <div className="pt-4 flex flex-col gap-3">
                                {!user ? (
                                    <>
                                        <Link
                                            to="/connexion"
                                            onClick={() => setOpen(false)}
                                            className="pill-btn pill-btn--outline justify-center"
                                        >
                                            <LogIn size={14} /> Se connecter
                                        </Link>
                                        <Link
                                            to="/inscription"
                                            onClick={() => setOpen(false)}
                                            className="pill-btn justify-center"
                                        >
                                            <UserPlus size={14} /> Demander l'accès
                                        </Link>
                                    </>
                                ) : (
                                    <button
                                        onClick={doLogout}
                                        className="pill-btn pill-btn--outline justify-center"
                                        data-testid="nav-mobile-logout"
                                    >
                                        <LogOut size={14} /> Déconnexion ({user.prenom})
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </header>

            <main className="flex-1">
                <Outlet />
            </main>

            {/* Partners strip (gold background, black cards) — visible on all pages */}
            <PartnersStrip />

            {/* Floating AI chat widget (approved pros + admin only) */}
            <ChatWidget />

            <footer
                className="border-t"
                style={{ borderColor: "var(--bc-border)", background: "#000" }}
                data-testid="site-footer"
            >
                <div className="max-w-7xl mx-auto px-5 md:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
                    <Logo size="sm" href={null} />
                    <div className="flex flex-wrap gap-6 text-xs uppercase tracking-[0.16em]">
                        <a
                            href="https://boucherie-costa.ch/politique-de-confidentialite/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-[#A0A0A0] hover:text-[#D4BC7C]"
                            data-testid="footer-privacy"
                        >
                            Politique de confidentialité
                        </a>
                        <span className="text-[#A0A0A0]">© {new Date().getFullYear()} Boucherie Costa</span>
                    </div>
                </div>
            </footer>
        </div>
    );
}
